/**
 * 공고 찾기 주소(query) 상태 SSoT — 검색어·필터·정렬·보기를 주소에 담아 새로고침·뒤로가기·공유 시 같은 화면을 복원한다(FR-004).
 * 기본값은 주소에서 생략한다. 기존 "공고" 목록·"AI 검색" 주소 변환(FR-005·Edge)도 여기서 소유한다.
 */
import { ROUTES } from '@/config/routes';
import { isRegionSido, REGION_SIDO_OPTIONS } from '@/config/region';
import { parsePositiveInt } from '@/lib/routeParams';
import type { ApplyStatus } from '@/types/search';

import { STATUS_OPTIONS } from './searchDisplay';

import type { DeadlineRange, FinderFilter, FinderSort, FinderView, FinderWay } from '../types';

export const FINDER_PAGE_SIZE = 20;

/** 접수 상태 기본값(FR-010) — 마감만 제외(시기 정보 없음도 숨기지 않는다, specs/093). 백엔드 기본값과 같다. */
export const DEFAULT_STATUSES: readonly ApplyStatus[] = STATUS_OPTIONS.filter(
  (s) => s !== 'CLOSED',
);
const DEADLINES: readonly DeadlineRange[] = ['D7', 'D30', 'D90'];

/** 주소 키 — 기존 "공고" 목록 키(`region`·`supportField`·`beneficiaryType`·`includeClosed`·`keyword`)와 겹치지 않게 복수형을 쓴다. */
const KEY = {
  query: 'q',
  recommendedOnly: 'recommended',
  statuses: 'statuses',
  deadline: 'deadline',
  categories: 'categories',
  beneficiaries: 'beneficiaries',
  regions: 'regions',
  showHidden: 'hidden',
  showOtherYears: 'otherYears',
  sort: 'sort',
  view: 'view',
  way: 'way',
} as const;

export interface FinderUrlState {
  query: string;
  filter: FinderFilter;
  sort: FinderSort;
  /** 주소에 보기가 명시된 경우만 — 없으면 브라우저 기억 값 → 모드 기본값 순(FR-003) */
  view: FinderView | null;
  /** 찾는 방식 — 기본 announcement 는 주소에서 생략 */
  way: FinderWay;
}

export function createDefaultFilter(): FinderFilter {
  return {
    recommendedOnly: false,
    statuses: [],
    deadline: null,
    categories: [],
    beneficiaries: [],
    regions: [],
    showHiddenRegions: false,
    showOtherYears: false,
  };
}

/** 빈 배열과 기본 3종 선택을 같은 상태로 본다 — 둘 다 서버 기본값이다. */
export function isDefaultStatuses(statuses: readonly ApplyStatus[]): boolean {
  return (
    statuses.length === 0 ||
    (statuses.length === DEFAULT_STATUSES.length &&
      DEFAULT_STATUSES.every((s) => statuses.includes(s)))
  );
}

/** 화면 표시용 접수 상태 선택값 — 기본값이면 기본 3종이 체크돼 보인다(FR-010 수용 6). */
export function effectiveStatuses(statuses: readonly ApplyStatus[]): ApplyStatus[] {
  return statuses.length === 0 ? [...DEFAULT_STATUSES] : [...statuses];
}

export function hasActiveFilter(filter: FinderFilter): boolean {
  return (
    filter.recommendedOnly ||
    !isDefaultStatuses(filter.statuses) ||
    filter.deadline !== null ||
    filter.categories.length > 0 ||
    filter.beneficiaries.length > 0 ||
    filter.regions.length > 0
  );
}

function uniq(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function isStatus(v: string): v is ApplyStatus {
  return (STATUS_OPTIONS as readonly string[]).includes(v);
}

function isDeadline(v: string | null): v is DeadlineRange {
  return v !== null && (DEADLINES as readonly string[]).includes(v);
}

function parseWay(raw: string | null): FinderWay {
  return raw === 'company' || raw === 'program' ? raw : 'announcement';
}

export function parseFinderParams(params: URLSearchParams): FinderUrlState {
  const deadline = params.get(KEY.deadline);
  const view = params.get(KEY.view);
  return {
    query: (params.get(KEY.query) ?? '').trim(),
    filter: {
      recommendedOnly: params.get(KEY.recommendedOnly) === '1',
      statuses: uniq(params.getAll(KEY.statuses)).filter(isStatus),
      deadline: isDeadline(deadline) ? deadline : null,
      categories: uniq(params.getAll(KEY.categories)),
      beneficiaries: uniq(params.getAll(KEY.beneficiaries)),
      regions: uniq(params.getAll(KEY.regions)).filter(isRegionSido),
      showHiddenRegions: params.get(KEY.showHidden) === '1',
      showOtherYears: params.get(KEY.showOtherYears) === '1',
    },
    sort: params.get(KEY.sort) === 'DEADLINE' ? 'DEADLINE' : 'LATEST',
    view: view === 'table' || view === 'cards' ? view : null,
    way: parseWay(params.get(KEY.way)),
  };
}

export function buildFinderParams(state: FinderUrlState): URLSearchParams {
  const params = new URLSearchParams();
  const { query, filter, sort, view, way } = state;
  if (query) params.set(KEY.query, query);
  if (filter.recommendedOnly) params.set(KEY.recommendedOnly, '1');
  if (!isDefaultStatuses(filter.statuses)) {
    filter.statuses.forEach((s) => params.append(KEY.statuses, s));
  }
  if (filter.deadline) params.set(KEY.deadline, filter.deadline);
  filter.categories.forEach((c) => params.append(KEY.categories, c));
  filter.beneficiaries.forEach((b) => params.append(KEY.beneficiaries, b));
  filter.regions.forEach((r) => params.append(KEY.regions, r));
  // 숨김 보기는 지역 필터가 있을 때만 의미가 있다.
  if (filter.showHiddenRegions && filter.regions.length > 0) params.set(KEY.showHidden, '1');
  if (filter.showOtherYears) params.set(KEY.showOtherYears, '1');
  if (sort !== 'LATEST') params.set(KEY.sort, sort);
  if (view) params.set(KEY.view, view);
  if (way !== 'announcement') params.set(KEY.way, way);
  return params;
}

// ── 기존 주소 변환(FR-005·Edge "기존 공고 주소·북마크로 진입") ──────────────────────────────

const LEGACY_LIST_KEYS = [
  'region',
  'supportField',
  'beneficiaryType',
  'includeClosed',
  'keyword',
  'page',
] as const;

/**
 * 구 "공고" 목록 필터의 지역 값(수집처 원문 축약형) → 표준 광역. 북마크 호환 전용 — 정규화 규칙의 SSoT 는 백엔드
 * `RegionNormalizer` 이며, 여기서는 그 별칭 중 두 글자 축약만 받는다(나머지는 앞부분 일치로 찾는다).
 */
const LEGACY_REGION_ABBR: Record<string, string> = {
  충북: '충청북도',
  충남: '충청남도',
  전북: '전북특별자치도',
  전남: '전라남도',
  경북: '경상북도',
  경남: '경상남도',
};

function toStandardRegion(raw: string): string | null {
  const value = raw.trim();
  if (isRegionSido(value)) return value;
  if (LEGACY_REGION_ABBR[value]) return LEGACY_REGION_ABBR[value];
  const prefix = value.replace(/(시|도)$/, '');
  const matches = REGION_SIDO_OPTIONS.filter((r) => prefix.length >= 2 && r.startsWith(prefix));
  return matches.length === 1 ? matches[0] : null;
}

/**
 * 구 "공고" 목록 주소(`/workspace/announcements?region=…`)를 공고 찾기 주소로 바꾼다. 구 키가 없으면 null.
 * `supportField`(수집처 분류 원문 → 묶음 분야)는 대응표가 서버에만 있어 버린다 — 다른 조건은 유지되고 분야만 "전체"로 열린다.
 */
export function migrateLegacyListParams(params: URLSearchParams): URLSearchParams | null {
  if (!LEGACY_LIST_KEYS.some((k) => params.has(k))) return null;
  const state = parseFinderParams(params);
  const region = params.get('region');
  const standard = region ? toStandardRegion(region) : null;
  if (standard) state.filter.regions = uniq([...state.filter.regions, standard]);
  const beneficiary = params.get('beneficiaryType')?.trim();
  if (beneficiary) state.filter.beneficiaries = uniq([...state.filter.beneficiaries, beneficiary]);
  if (params.get('includeClosed') === 'true') {
    state.filter.statuses = uniq([...effectiveStatuses(state.filter.statuses), 'CLOSED']).filter(
      isStatus,
    );
  }
  const keyword = params.get('keyword')?.trim();
  if (keyword && !state.query) state.query = keyword;
  return buildFinderParams(state);
}

/**
 * 구 "AI 검색"(`/workspace/search`) 주소 → 새 진입점. 고객사 기준은 내 업체 "맞는 공고" 탭, 지원사업 기준은 공고 상세의
 * "지원 가능한 내 고객사" 섹션, 그 밖(통합 검색)은 같은 검색어의 공고 찾기로 잇는다.
 */
export function legacySearchTarget(params: URLSearchParams): string {
  const tab = params.get('tab');
  const query = (params.get('q') ?? '').trim();
  const companyId = parsePositiveInt(params.get('cid'));
  const announcementId = parsePositiveInt(params.get('aid'));
  if (tab === 'company' && companyId !== null) {
    return ROUTES.workspace.companyMatch(companyId, query || undefined);
  }
  if (tab === 'program' && announcementId !== null) {
    return ROUTES.workspace.announcementCandidates(announcementId);
  }
  const next = query ? `?${new URLSearchParams({ q: query })}` : '';
  return `${ROUTES.workspace.announcements}${next}`;
}
